import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';

interface PaymentInstructionsData {
  amount: number;
  tokenAmount: number;
  phase: number;
  pricePerToken: string;
  paymentMethod: string;
  email: string;
  phone: string;
  invoiceId: string;
  virtualAccount?: string;
  expiryTime: string;
}

export const PaymentInstructionsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const paymentData = (location.state as PaymentInstructionsData) || null;

  const [timeRemaining, setTimeRemaining] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!paymentData) {
      navigate('/presale');
      return;
    }

    const updateTimer = () => {
      const now = Date.now();
      const expiry = new Date(paymentData.expiryTime).getTime();
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeRemaining('Expired');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeRemaining(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [paymentData, navigate]);

  if (!paymentData) return null;

  const totalIDR = paymentData.amount * 15800;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getPaymentInstructions = () => {
    const method = paymentData.paymentMethod;

    if (['BCA', 'BNI', 'Mandiri', 'BRI', 'Permata'].includes(method)) {
      return {
        type: 'Bank Transfer',
        icon: 'university',
        steps: [
          'Login to your internet banking or mobile banking app',
          `Select Transfer to Other Bank or ${method} Transfer`,
          `Enter the Virtual Account number: ${paymentData.virtualAccount}`,
          `Enter the amount: ${totalIDR.toLocaleString('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 })}`,
          'Confirm the transaction details',
          'Complete the payment',
          'Save your transaction receipt',
        ],
      };
    }

    if (['OVO', 'DANA', 'GoPay', 'LinkAja', 'ShopeePay'].includes(method)) {
      return {
        type: 'E-Wallet',
        icon: 'wallet',
        steps: [
          `Open your ${method} app`,
          'Select "Pay" or "Transfer"',
          'Scan the QR code below or enter payment code',
          `Confirm payment amount: ${totalIDR.toLocaleString('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 })}`,
          'Enter your PIN',
          'Complete the payment',
        ],
      };
    }

    if (['Alfamart', 'Indomaret'].includes(method)) {
      return {
        type: 'Retail Store',
        icon: 'store',
        steps: [
          `Visit the nearest ${method} store`,
          'Tell the cashier you want to make a payment',
          `Show your payment code: ${paymentData.invoiceId}`,
          `Pay the amount: ${totalIDR.toLocaleString('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 })}`,
          'Get your payment receipt',
        ],
      };
    }

    return {
      type: 'Credit/Debit Card',
      icon: 'credit-card',
      steps: [
        'Enter your card details',
        'Verify the payment amount',
        'Complete OTP verification',
        'Payment will be processed immediately',
      ],
    };
  };

  const instructions = getPaymentInstructions();

  return (
    <div className="min-h-screen bg-bg-primary py-20">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/10 mb-4">
            <i className="fas fa-clock text-3xl text-gold"></i>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Complete Your Payment</h1>
          <p className="text-text-secondary">Please complete payment before the time expires</p>
        </div>

        {/* Timer */}
        <Card className="p-6 mb-6 bg-gradient-to-r from-gold/10 to-transparent border-gold/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-secondary mb-1">Payment expires in</p>
              <p className="text-3xl font-bold gradient-text">{timeRemaining}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-text-secondary mb-1">Invoice ID</p>
              <p className="text-lg font-mono font-semibold text-white">{paymentData.invoiceId}</p>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Payment Instructions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Payment Details */}
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <i className={`fas fa-${instructions.icon} text-2xl text-gold`}></i>
                <div>
                  <h2 className="text-xl font-semibold text-white">{instructions.type}</h2>
                  <p className="text-sm text-text-secondary">{paymentData.paymentMethod}</p>
                </div>
              </div>

              {paymentData.virtualAccount && (
                <div className="mb-6 p-4 bg-surface border border-border rounded-lg">
                  <p className="text-sm text-text-secondary mb-2">Virtual Account Number</p>
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-2xl font-mono font-bold text-white">{paymentData.virtualAccount}</p>
                    <button
                      onClick={() => copyToClipboard(paymentData.virtualAccount!)}
                      className="px-4 py-2 bg-gold/10 hover:bg-gold/20 text-gold rounded-lg transition-colors flex items-center gap-2"
                    >
                      <i className={`fas fa-${copied ? 'check' : 'copy'}`}></i>
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}

              <div className="mb-6 p-4 bg-surface border border-border rounded-lg">
                <p className="text-sm text-text-secondary mb-2">Total Payment</p>
                <p className="text-3xl font-bold gradient-text">
                  {totalIDR.toLocaleString('id-ID', {
                    style: 'currency',
                    currency: 'IDR',
                    minimumFractionDigits: 0,
                  })}
                </p>
              </div>

              {/* QR Code for E-Wallet */}
              {['OVO', 'DANA', 'GoPay', 'LinkAja', 'ShopeePay'].includes(paymentData.paymentMethod) && (
                <div className="mb-6 flex justify-center">
                  <div className="p-6 bg-white rounded-lg">
                    <div className="w-48 h-48 bg-gray-200 flex items-center justify-center">
                      <i className="fas fa-qrcode text-6xl text-gray-400"></i>
                    </div>
                    <p className="text-center text-sm text-gray-600 mt-2">Scan QR Code to Pay</p>
                  </div>
                </div>
              )}
            </Card>

            {/* Step by Step Instructions */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Payment Instructions</h3>
              <div className="space-y-3">
                {instructions.steps.map((step, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gold/10 text-gold flex items-center justify-center font-semibold text-sm">
                      {index + 1}
                    </div>
                    <p className="text-text-secondary pt-1">{step}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Important Notes */}
            <Card className="p-6 bg-surface border-gold/20">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <i className="fas fa-exclamation-circle text-gold"></i>
                Important Notes
              </h3>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex gap-2">
                  <i className="fas fa-check text-gold mt-1"></i>
                  <span>Payment must be completed before the expiry time</span>
                </li>
                <li className="flex gap-2">
                  <i className="fas fa-check text-gold mt-1"></i>
                  <span>Make sure to pay the exact amount shown</span>
                </li>
                <li className="flex gap-2">
                  <i className="fas fa-check text-gold mt-1"></i>
                  <span>Tokens will be credited to your wallet after payment confirmation (usually within 5-15 minutes)</span>
                </li>
                <li className="flex gap-2">
                  <i className="fas fa-check text-gold mt-1"></i>
                  <span>Keep your payment receipt for reference</span>
                </li>
                <li className="flex gap-2">
                  <i className="fas fa-check text-gold mt-1"></i>
                  <span>Contact support if payment is not confirmed within 1 hour</span>
                </li>
              </ul>
            </Card>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-24">
              <h3 className="text-lg font-semibold text-white mb-4">Order Summary</h3>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Phase</span>
                  <span className="text-white font-medium">Phase {paymentData.phase}</span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Token Amount</span>
                  <span className="text-white font-medium">{paymentData.tokenAmount.toLocaleString()} IDNG</span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Price per Token</span>
                  <span className="text-white font-medium">${paymentData.pricePerToken}</span>
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-text-secondary">Email</span>
                    <span className="text-white text-xs">{paymentData.email}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">Phone</span>
                    <span className="text-white">{paymentData.phone}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() => {
                    alert('In a real implementation, this would check payment status via Xendit API');
                  }}
                >
                  <i className="fas fa-sync-alt mr-2"></i>
                  Check Payment Status
                </Button>

                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => navigate('/presale')}
                >
                  <i className="fas fa-arrow-left mr-2"></i>
                  Back to Presale
                </Button>
              </div>

              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-xs text-text-tertiary text-center">
                  Need help? Contact our support team
                </p>
                <div className="mt-3 flex justify-center gap-4">
                  <a href="#" className="text-gold hover:text-gold-light text-sm">
                    <i className="fas fa-envelope mr-1"></i>
                    Email
                  </a>
                  <a href="#" className="text-gold hover:text-gold-light text-sm">
                    <i className="fab fa-whatsapp mr-1"></i>
                    WhatsApp
                  </a>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
